import User from '@/store/models/user/User'
import Notification from '@/store/models/user/Notification'
import * as NOTIF from '@/utils/constants/notification-types'
import { getConnections } from '@/api/user'
import { oauthUnlink } from '@/api/auth'
import Item from '@/store/models/user/Item'
import Trade from '@/store/models/user/Trade'

export default {
  async getAuthUrl() {
    const { url } = await this.$api.auth.getAuthUrl()
    return url
  },
  async logout({ commit }) {
    await this.$api.auth.logout()

    commit('setSession', null)
    commit('setBanned', false)
    commit('setInventory', [])
    this._vm.$socket.reconnect()
  },
  async getInfo({ commit, getters }) {
    // NOTE does not include balance
    const user = await this.$api.user.get()
    const localUser = getters.session

    commit('setSession', new User({ ...localUser, ...user }))
  },
  // deposit
  async getSteamInventory(_, type) {
    return this.$api.user.getSteamInventory(type)
  },
  async deposit({ dispatch }, assetids) {
    if (assetids?.length < 1) throw new Error('InvalidParams')

    const { tradeId } = await this.$api.user.deposit(assetids)

    dispatch('getPendingTrades')

    return tradeId
  },
  async getDepositAddress({ commit }, coin) {
    const address = await this.$api.user.getDepositAddress(coin)
    return address
  },
  async getWithdrawable({ commit }) {
    return this.$api.user.getWithdrawable()
  },
  // inventory
  async getInventory({ commit, rootGetters }) {
    const resp = await this.$api.user.getInventory()

    const itemdb = rootGetters['items/items']
    const items = resp.map(i => new Item({ ...i, ...itemdb[i.name] }))

    commit('setInventory', items)
  },
  async sellItems({ commit }, ids) {
    const { ids: removedIds, newBetRequirement } = await this.$api.user.sellItems(ids)

    commit('removeItems', removedIds)
    commit('setUserProp', ['betRequirement', newBetRequirement])
  },
  async swapItems({ commit, rootGetters }, data) {
    const { removed, added } = await this.$api.user.swapItems(data)
    commit('removeItems', removed)

    const itemdb = rootGetters['items/items']
    const items = added.map(i => new Item({ ...i, ...itemdb[i.name], isNew: true }))
    commit('addItems', items)
  },
  async withdrawItems({ commit }, ids) {
    const removedIds = await this.$api.user.withdrawItems(ids)
    commit('removeItems', removedIds)

    return removedIds
  },
  async checkItemStock({ commit }, ids) {
    const items = await this.$api.user.checkItemStock(ids)
    // commit('setItemsStockInfo', items)

    return items
  },
  // settings
  async setTradeUrl({ commit }, tradeUrl) {
    const url = await this.$api.user.setTradeUrl(tradeUrl)
    commit('setUserProp', ['tradeUrl', url])

    Notification.delete(NOTIF.SETTINGS_TRADEURL_MISSING)
  },
  async setName({ commit }, name) {
    const cleanName = await this.$api.user.setName(name)
    commit('setUserProp', ['name', cleanName])
  },
  async setEmail({ commit }, email) {
    const { email: newEmail } = await this.$api.user.setEmail(email)

    if (newEmail || newEmail === null) {
      commit('setUserProp', ['email', newEmail])
      commit('setUserProp', ['emailVerified', false])
    }

    return newEmail
  },
  async getActiveSeedPair({ commit }) {
    return this.$api.user.getActiveSeedPair()
  },
  async createSeedPair({ commit }, clientSecret) {
    return this.$api.user.createSeedPair(clientSecret)
  },
  // connections
  async getConnections({ commit }) {
    const connections = await getConnections()
    commit('setConnections', connections)
  },
  async unlinkConnection({ commit }, provider) {
    await oauthUnlink(provider)
    commit('removeConnection', provider)
  },
  // trades
  async getPendingTrades({ commit, getters }) {
    const { entries: trades } = await this.$api.user.getPendingTrades({
      pending: true,
      page: 1,
      limit: 25,
      sortBy: 'createdAt',
      asc: false,
    })

    // delete all other trades in state
    await Trade.create({ data: trades })

    // show notification dot
    if (getters.pendingTrades.length) {
      Notification.insertOrUpdate({
        data: {
          id: NOTIF.TRADE_ACTIVE,
          type: NOTIF.TRADE_ACTIVE,
          action: {
            type: 'modal',
            action: 'open',
            name: 'account',
            settings: { maxWidth: 1000 },
            payload: { tab: 'trades' },
          },
          persistent: true,
        },
      })
    } else {
      Notification.delete(NOTIF.TRADE_ACTIVE)
    }

    return trades
  },
  // faucet
  async claimFaucet({ commit }, token) {
    const { faucetLastRedeemed, amount } = await this.$api.user.claimFaucet(token)
    commit('setUserProp', ['faucetLastRedeemed', faucetLastRedeemed])

    return { amount, faucetLastRedeemed }
  },
  // affs
  async redeemReferralCode({ commit }, code) {
    const { id: codeId, receivedReward, newBetRequirement } = await this.$api.user.redeemReferralCode(code)

    commit('setUserProp', ['affiliateOf', codeId])

    // first time use
    if (receivedReward) {
      commit('addOrRemoveBalance', receivedReward)
      commit('setUserProp', ['referredBy', codeId])
    }

    if (newBetRequirement) {
      commit('setUserProp', ['betRequirement', newBetRequirement])
    }

    return { codeId, reward: receivedReward }
  },
  // level rewards
  async claimLevelRewards({ commit }) {
    const resp = await this.$api.user.claimLevelRewards()
    commit('setUserProp', ['claimedLevelRewards', resp.claimedLevelRewards])
    commit('setRewards', resp.rewards)

    Notification.delete(NOTIF.LEVEL_UP)

    return resp
  },
  async getUserRewards({ commit }) {
    const rewards = await this.$api.user.getUserRewards()
    commit('setRewards', rewards)

    return rewards
  },
  async claimUserReward({ commit, rootGetters }, id) {
    const rewardData = await this.$api.user.claimUserReward(id)

    if (rewardData.scrap) {
      commit('addOrRemoveBalance', rewardData.scrap)
    }

    if (rewardData.items) {
      const itemdb = rootGetters['items/items']
      const items = rewardData.items.map(i => new Item({ ...i, ...itemdb[i.name], isNew: true }))

      commit('addItems', items)
    }

    commit('removeReward', id)

    return rewardData
  },
  async getLevelCaseCooldown({ commit }) {
    const cooldown = await this.$api.user.getLevelCaseCooldown()
    commit('setLevelCaseCooldown', cooldown)

    return cooldown
  },
  async openLevelCase({ commit }, tier) {
    return this.$api.user.openLevelCase(tier)
  },
}
